// NativeSRSView — Hisni native landing page (2026-05-14 rewrite).
// Replaces iframe approach which broke on CSP frame-ancestors:'none' from
// /identity/. This is the Phase 1 native landing — shows live prayer info,
// Hijri date, SR stats, and the SurahBrowser/QuranReader native stack.
//
// Phase 1b: adds Stats tab in the stats block header → renders StatsView in
// place. Back button returns to home landing (three-screen stack home/stats
// over SurahBrowser↔QuranReader).
import type { AweniContext } from '../../types';
import { computeSRSStats, seedAyahCards } from './srs/IndexedDBBridge';
import { prayerTimeService } from '../../services/PrayerTimeService';
import { gregorianToHijri, formatHijri } from '../../services/HijriDate';
import { SurahBrowser } from './SurahBrowser';
import { QuranReader } from './QuranReader';
import { mountStatsView } from './StatsView';

interface SRSStats { total: number; dueToday: number; mastered: number; lastSync?: string | null }

export async function mountNativeSRSView(
  container: HTMLElement,
  ctx: AweniContext,
): Promise<() => void> {
  const t = (k: string, fb: string) => ctx.i18n.t(k) || fb;
  const locale = ctx.i18n.locale || 'en';
  let statsUnmount: (() => void) | null = null;
  let surahBrowser: SurahBrowser | null = null;
  let quranReader: QuranReader | null = null;

  const destroyChildren = (): void => {
    statsUnmount?.();
    statsUnmount = null;
    surahBrowser?.destroy();
    surahBrowser = null;
    quranReader?.destroy();
    quranReader = null;
  };

  const showStats = async (): Promise<void> => {
    destroyChildren();
    container.innerHTML = '';
    injectStyles();

    const header = document.createElement('div');
    header.className = 'hisni-stats-topbar';
    header.innerHTML = `
      <button class="hisni-back-btn" aria-label="${escHtml(t('common_cancel','Back'))}">
        ← ${escHtml(t('common_cancel','Back'))}
      </button>
      <span class="hisni-stats-topbar-title">${escHtml(t('hisni_stats_title','My Progress'))}</span>`;
    container.appendChild(header);

    const statsContainer = document.createElement('div');
    container.appendChild(statsContainer);

    header.querySelector('.hisni-back-btn')?.addEventListener('click', () => {
      statsUnmount?.();
      statsUnmount = null;
      void showHome();
    });

    statsUnmount = await mountStatsView(statsContainer, ctx);
  };

  const showHome = async (): Promise<void> => {
    destroyChildren();

    let stats: SRSStats = { total: 0, dueToday: 0, mastered: 0 };
    try { stats = await computeSRSStats(); } catch { /* empty stats ok */ }

    let prayerName = '', prayerCountdown = '';
    try {
      const next = prayerTimeService.getNextPrayer();
      if (next) {
        prayerName = t(`prayer_${next.name.toLowerCase()}`, next.name);
        const mins = Math.max(0, Math.round(next.minutesUntil));
        if (mins < 60) prayerCountdown = `${t('prayer_in','in')} ${mins} ${t('prayer_min_short','min')}`;
        else {
          const h = Math.floor(mins / 60), m = mins % 60;
          prayerCountdown = `${t('prayer_in','in')} ${h}${t('prayer_hr_short','h')} ${m}${t('prayer_min_short','m')}`;
        }
      }
    } catch { /* prayer service not ready */ }

    let hijriStr = '';
    try { hijriStr = formatHijri(gregorianToHijri(), locale); } catch { /* skip */ }

    injectStyles();

    container.innerHTML = `
      <div class="hisni-native">
        <header class="hisni-hero">
          <div class="hisni-hero-greeting">${escHtml(t('hisni_native_title','Hisni — your spiritual companion'))}</div>
          <div class="hisni-hero-info">
            ${hijriStr ? `<div class="hisni-info-pill">📅 ${escHtml(hijriStr)}</div>` : ''}
            ${prayerName ? `<div class="hisni-info-pill hisni-info-pill--accent">
              🕌 ${escHtml(prayerName)} ${prayerCountdown ? '· ' + escHtml(prayerCountdown) : ''}
            </div>` : ''}
          </div>
        </header>

        <section class="hisni-stats-block">
          <div class="hisni-stats-header">
            <div class="hisni-stats-title">${escHtml(t('hisni_native_srs_title','Memorization progress'))}</div>
            <button id="hisni-stats-btn" class="hisni-btn-stats" aria-label="${escHtml(t('hisni_stats_title','My Progress'))}">
              📊 ${escHtml(t('hisni_stats_title','Stats'))}
            </button>
          </div>
          <div class="hisni-stats-grid">
            <div class="hisni-stat"><div class="hisni-stat-n">${stats.total}</div><div class="hisni-stat-l">${escHtml(t('hisni_native_total','Total'))}</div></div>
            <div class="hisni-stat"><div class="hisni-stat-n">${stats.dueToday}</div><div class="hisni-stat-l">${escHtml(t('hisni_native_due','Due today'))}</div></div>
            <div class="hisni-stat"><div class="hisni-stat-n">${stats.mastered}</div><div class="hisni-stat-l">${escHtml(t('hisni_native_mastered','Mastered'))}</div></div>
          </div>
          ${stats.lastSync
            ? `<div class="hisni-sync-line">${escHtml(t('hisni_native_last_sync','Last sync'))}: ${escHtml(new Date(stats.lastSync).toLocaleString(locale))}</div>`
            : ''}
          <button id="hisni-sync-btn" class="hisni-btn-sync">${escHtml(t('hisni_native_sync','Sync from server'))}</button>
        </section>

        <section class="hisni-sb-section">
          <div id="hisni-surah-browser"></div>
        </section>
      </div>
    `;

    container.querySelector<HTMLButtonElement>('#hisni-stats-btn')?.addEventListener('click', () => {
      void showStats();
    });

    const token = ctx.auth.getToken() ?? undefined;
    const syncBtn = container.querySelector<HTMLButtonElement>('#hisni-sync-btn');
    syncBtn?.addEventListener('click', async () => {
      syncBtn.disabled = true;
      syncBtn.textContent = t('hisni_native_syncing','Syncing...');
      try {
        const res = await fetch('/aweni/api/v1/gateway/memoriz/api/reviews/due', {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        await seedAyahCards(Array.isArray(data) ? data : (data.cards ?? data.data ?? []));
        try {
          const updated = await computeSRSStats();
          const grid = container.querySelector('.hisni-stats-grid');
          if (grid) {
            grid.innerHTML = `
              <div class="hisni-stat"><div class="hisni-stat-n">${updated.total}</div><div class="hisni-stat-l">${escHtml(t('hisni_native_total','Total'))}</div></div>
              <div class="hisni-stat"><div class="hisni-stat-n">${updated.dueToday}</div><div class="hisni-stat-l">${escHtml(t('hisni_native_due','Due today'))}</div></div>
              <div class="hisni-stat"><div class="hisni-stat-n">${updated.mastered}</div><div class="hisni-stat-l">${escHtml(t('hisni_native_mastered','Mastered'))}</div></div>`;
          }
        } catch { /* stats refresh failed — non-critical */ }
        syncBtn.textContent = t('hisni_native_sync_ok','Synced ✓');
      } catch {
        syncBtn.textContent = t('hisni_native_sync_error','Sync failed');
      } finally {
        syncBtn.disabled = false;
        setTimeout(() => { syncBtn.textContent = t('hisni_native_sync','Sync from server'); }, 3000);
      }
    });

    const sbContainer = container.querySelector<HTMLElement>('#hisni-surah-browser');
    if (sbContainer) showSurahBrowser(sbContainer);
  };

  function showSurahBrowser(sbContainer: HTMLElement): void {
    surahBrowser = new SurahBrowser(sbContainer, ctx, (surahNumber: number) => {
      surahBrowser?.destroy();
      surahBrowser = null;
      quranReader = new QuranReader(sbContainer, surahNumber, ctx, () => {
        quranReader?.destroy();
        quranReader = null;
        showSurahBrowser(sbContainer);
      });
      void quranReader.init();
    });
  }

  await showHome();

  return () => {
    destroyChildren();
    container.innerHTML = '';
  };
}

function injectStyles(): void {
  if (document.getElementById('hisni-native-styles')) return;
  const s = document.createElement('style');
  s.id = 'hisni-native-styles';
  s.textContent = `
    .hisni-native { max-width: 720px; margin: 0 auto; padding: var(--ln-space-3); padding-bottom: 80px; }

    .hisni-hero {
      background: linear-gradient(135deg, var(--ln-accent, #0A0A0A), var(--ln-accent-secondary, #C8A96A));
      color: var(--ln-accent-text, #fff);
      border-radius: var(--ln-radius-lg, 16px);
      padding: 18px 16px;
      margin-bottom: 14px;
    }
    .hisni-hero-greeting { font-size: 1rem; font-weight: 700; margin-bottom: 8px; }
    .hisni-hero-info { display: flex; gap: 6px; flex-wrap: wrap; }
    .hisni-info-pill {
      font-size: .78rem; padding: 4px 10px; background: rgba(255,255,255,.18);
      border-radius: var(--ln-radius-full, 9999px); backdrop-filter: blur(8px);
    }
    .hisni-info-pill--accent { background: rgba(255,255,255,.28); font-weight: 600; }

    .hisni-stats-block {
      background: var(--ln-bg-card, #fff);
      border: 1px solid var(--ln-border, rgba(0,0,0,.08));
      border-radius: var(--ln-radius-lg, 16px);
      padding: 14px;
      margin-bottom: 14px;
    }
    .hisni-stats-header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 8px; }
    .hisni-stats-title { font-size: .75rem; font-weight: 700; color: var(--ln-text-secondary); text-transform: uppercase; letter-spacing: .5px; }
    .hisni-btn-stats {
      padding: 5px 12px;
      background: var(--ln-accent, #0A0A0A);
      color: var(--ln-accent-text, #fff);
      border: none;
      border-radius: var(--ln-radius-md, 10px);
      cursor: pointer;
      font-weight: 600;
      font-size: .8rem;
      transition: opacity var(--motion-duration-fast, 120ms) ease;
    }
    .hisni-btn-stats:hover { opacity: .85; }
    .hisni-btn-stats:focus-visible { outline: 2px solid var(--ln-accent, #0A0A0A); outline-offset: 2px; }
    .hisni-stats-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px; }
    .hisni-stat { text-align: center; }
    .hisni-stat-n { font-size: 1.6rem; font-weight: 800; color: var(--ln-accent, #0A0A0A); }
    .hisni-stat-l { font-size: .7rem; color: var(--ln-text-secondary); text-transform: uppercase; letter-spacing: .4px; }
    .hisni-sync-line { font-size: .7rem; color: var(--ln-text-secondary); margin-top: 8px; text-align: center; }
    .hisni-btn-sync {
      width: 100%; margin-top: 10px; padding: 8px;
      background: transparent; color: var(--ln-accent, #0A0A0A);
      border: 1px solid var(--ln-border, rgba(0,0,0,.15));
      border-radius: var(--ln-radius-md, 10px); cursor: pointer;
      font-weight: 600; font-size: .85rem;
    }
    .hisni-btn-sync:hover { background: var(--ln-bg-subtle, rgba(0,0,0,.04)); }
    .hisni-btn-sync:disabled { opacity: .6; cursor: wait; }

    .hisni-sb-section { margin-bottom: 14px; }

    .hisni-stats-topbar {
      display: flex; align-items: center; gap: 10px;
      padding: 10px var(--ln-space-3, 12px);
      border-bottom: 1px solid var(--ln-border, rgba(0,0,0,.08));
      margin-bottom: 2px;
    }
    .hisni-back-btn {
      background: transparent; border: none; cursor: pointer;
      font-size: .9rem; color: var(--ln-accent, #0A0A0A); font-weight: 600;
      padding: 6px 10px; border-radius: var(--ln-radius-md, 10px);
    }
    .hisni-back-btn:hover { background: var(--ln-bg-subtle, rgba(0,0,0,.04)); }
    .hisni-back-btn:focus-visible { outline: 2px solid var(--ln-accent, #0A0A0A); outline-offset: 2px; }
    .hisni-stats-topbar-title { font-size: .95rem; font-weight: 700; color: var(--ln-text-primary, #111); }

    .dark .hisni-stats-block {
      background: rgba(255,255,255,.04);
      border-color: rgba(255,255,255,.10);
    }
    .dark .hisni-stat-n { color: var(--ln-accent, #C8A96A); }
  `;
  document.head.appendChild(s);
}

function escHtml(s: string | undefined | null): string {
  if (s == null) return '';
  return String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}
